import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EditedRecord, PendingTimeOffRequest, RequestTypes } from '../../../models/TimeOffModels';
import { PTOConfig } from '../../../models/paid-time-off';
import { CompaniesService } from '../../../services/companies.service';
import {
  AreDatesEqual,
  AreDateTimesEqual,
  DoesRangeContainWeekends,
  GetDaysBetweenTwoDates,
  GetHoursBetweenTwoDates,
  GetHoursBetweenTwoTimes
} from '../../../utils/dateUtils';
import { map } from 'rxjs/operators';
import { AdminService } from '../../../services/admin.service';
import { TimeOffRequestService } from '../../../services/time-off-request.service';
import { isEqual, setDate, setMonth, setYear } from 'date-fns';
import { BehaviorSubject, Observable } from 'rxjs';
import { InstanceTypes } from '../../../models/instance-types';
import { ResetForm } from '@ngxs/form-plugin';
import { formatDatePickerValue, parseTimeFromDate } from '../../../utils/datepicker-value-parser';
import { Select, Store } from '@ngxs/store';
import {
  TimeOffRequestsState,
  TimeOffSetInitialApprovedRequestBalance,
  TimeOffSetInitialPendingChangeRequestBalance
} from '../../../state/TimeOffRequestsState/TimeOffRequests.state';
import { ModalService } from '../../../services/modal.service';

@Component({
  selector: 'lib-employee-request-edited',
  templateUrl: './employee-request-edited.component.html',
  styleUrls: ['./employee-request-edited.component.css'],
  standalone: false
})
export class EmployeeRequestEditedComponent implements OnInit, OnDestroy {
  @Input()
  public existingModel: EditedRecord;

  @Input()
  public clientCode: string;

  @Input()
  public openRequests: PendingTimeOffRequest[] = [];
  @Input()
  public editedRequests: EditedRecord[] = [];
  @Output()
  public modalClosed: EventEmitter<any> = new EventEmitter<any>();

  public timeOffPlans: PTOConfig[] = [];

  public showHolidayMessage = false;
  public currentPlanObs: BehaviorSubject<number>;
  public currentRequestHours: number = 0;
  public previousRequestHours: number = 0;
  @Select(TimeOffRequestsState.getCurrentRequestHours)
  public partialDayHours: Observable<number>;

  get startTimesEqual() {
    if (
      this.existingModel.EditedRecord.RequestType == RequestTypes.PartialDay &&
      this.existingModel.OriginalRecord != null
    ) {
      let currentEnd = this.existingModel.EditedRecord.StartDate;
      if (typeof currentEnd == 'string') {
        currentEnd = new Date(currentEnd);
      }
      let previousEnd = this.existingModel.OriginalRecord.StartDate;
      if (typeof previousEnd == 'string') {
        previousEnd = new Date(previousEnd);
      }
      currentEnd = setDate(currentEnd, previousEnd.getDate());
      currentEnd = setMonth(currentEnd, previousEnd.getMonth());
      currentEnd = setYear(currentEnd, previousEnd.getFullYear());
      return isEqual(currentEnd, previousEnd);
    }
    return true;
  }
  get endTimesEqual() {
    if (
      this.existingModel.EditedRecord.RequestType == RequestTypes.PartialDay &&
      this.existingModel.OriginalRecord != null
    ) {
      let currentEnd = this.existingModel.EditedRecord.EndDate;
      if (typeof currentEnd == 'string') {
        currentEnd = new Date(currentEnd);
      }
      let previousEnd = this.existingModel.OriginalRecord.EndDate;
      if (typeof previousEnd == 'string') {
        previousEnd = new Date(previousEnd);
      }
      currentEnd = setDate(currentEnd, previousEnd.getDate());
      currentEnd = setMonth(currentEnd, previousEnd.getMonth());
      currentEnd = setYear(currentEnd, previousEnd.getFullYear());
      return isEqual(currentEnd, previousEnd);
    }
    return true;
  }
  get isEmployeePortal() {
    return this.env.instanceType === InstanceTypes.EmployeePortal;
  }
  constructor(
    private companiesService: CompaniesService,
    private adminService: AdminService,
    private employeeTimeOffService: TimeOffRequestService,
    @Inject('env') private env: any,
    private store: Store,
    private modalService: ModalService
  ) {}

  ngOnInit() {
    this.currentPlanObs = new BehaviorSubject<number>(this.existingModel.EditedRecord.PlanId);
    this.companiesService.getPTOConfigs(this.clientCode).subscribe(configs => {
      this.timeOffPlans = configs;
    });

    this.store.dispatch(
      new ResetForm({
        path: 'timeOffRequests.NewRequestForm',
        value: {
          requestId: this.existingModel.EditedRecord.Id,
          timeOffType: this.existingModel.EditedRecord.PlanId,
          requestType: this.existingModel.EditedRecord.RequestType,
          startDate:
            this.existingModel.EditedRecord.StartDate != null
              ? formatDatePickerValue(this.existingModel.EditedRecord.StartDate)
              : null,
          hours: this._populateHoursField(),
          employeeComments: null,
          clientComments: null,
          startTime:
            this.existingModel.EditedRecord.RequestType == RequestTypes.PartialDay
              ? parseTimeFromDate(this.existingModel.EditedRecord.StartDate)
              : null,
          endTime:
            this.existingModel.EditedRecord.RequestType == RequestTypes.PartialDay
              ? parseTimeFromDate(this.existingModel.EditedRecord.EndDate)
              : null,
          dateRange: {
            start:
              this.existingModel.EditedRecord.RequestType == RequestTypes.MultipleDays
                ? this.existingModel.EditedRecord.StartDate
                : null,
            end:
              this.existingModel.EditedRecord.RequestType == RequestTypes.MultipleDays
                ? this.existingModel.EditedRecord.EndDate
                : null
          }
        }
      })
    );
    this.store.dispatch(
      new TimeOffSetInitialPendingChangeRequestBalance(this._initCurrentRequestHours())
    );
    this.currentRequestHours = this._initCurrentRequestHours();
    this.previousRequestHours = this._initPreviousRequestHours();
    if (this.existingModel.EditedRecord.RequestType == RequestTypes.MultipleDays) {
      let start = this.existingModel.EditedRecord.StartDate;
      if (typeof start == 'string') {
        start = new Date(this.existingModel.EditedRecord.StartDate);
      }
      let end = this.existingModel.EditedRecord.EndDate;
      if (typeof end == 'string') {
        end = new Date(this.existingModel.EditedRecord.EndDate);
      }
      this.adminService
        .getHolidays(start, end)
        .pipe(
          map(x => {
            return x.length > 0;
          })
        )
        .subscribe(result => {
          this.showHolidayMessage = result;
        });
    } else {
      let parsed = this.existingModel.EditedRecord.StartDate;
      if (typeof parsed == 'string') {
        parsed = new Date(parsed);
      }
      const start = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() - 1);
      const end = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() + 1);
      this.adminService
        .getHolidays(start, end)
        .pipe(
          map(x => {
            return x.length > 0;
          })
        )
        .subscribe(result => {
          this.showHolidayMessage = result;
        });
    }
  }

  ngOnDestroy() {
    this.store.dispatch(new TimeOffSetInitialPendingChangeRequestBalance(null));
  }

  public closeModal() {
    this.modalClosed.emit();
  }
  public isWeekend() {
    let start: Date | string;
    let end: Date | string;

    if (this.existingModel.EditedRecord.RequestType != RequestTypes.MultipleDays) {
      if (this.existingModel.EditedRecord.StartDate == null) return false;
      start = this.existingModel.EditedRecord.StartDate;
      end = start;
    } else {
      if (
        this.existingModel.EditedRecord.StartDate == null ||
        this.existingModel.EditedRecord.EndDate == null
      ) {
        return false;
      }
      start = this.existingModel.EditedRecord.StartDate;
      end = this.existingModel.EditedRecord.EndDate;
    }
    return DoesRangeContainWeekends(start, end);
  }
  protected readonly RequestTypes = RequestTypes;

  public cancelRequest() {
    const sub = this.modalService.confirmModalCallback.subscribe(result => {
      if (result) {
        this.employeeTimeOffService
          .cancelEditedRequest(
            this.clientCode,
            this.existingModel.EditedRecord.PersonId,
            this.existingModel.Request.Id
          )
          .subscribe(_ => {
            this.closeModal();
          });
      }
      sub.unsubscribe();
    });
    this.modalService.showConfirmModal(
      'Are you sure you want to cancel this request?',
      'No',
      'Yes',
      'Confirm Cancellation'
    );
  }

  public acceptRequest() {
    this.employeeTimeOffService
      .acceptEditedRequest(
        this.clientCode,
        this.existingModel.EditedRecord.PersonId,
        this.existingModel.Request.Id
      )
      .subscribe(_ => {
        this.closeModal();
      });
  }
  private _populateHoursField() {
    const requestType: RequestTypes = this.existingModel.EditedRecord.RequestType;
    switch (requestType) {
      case RequestTypes.PartialDay:
        return null;
      case RequestTypes.SingleDay:
        return this.existingModel.EditedRecord.TimeOffDays[0].Hours;
      case RequestTypes.MultipleDays:
        return this.existingModel.EditedRecord.TimeOffDays[0].Hours;
    }
  }
  private _initCurrentRequestHours() {
    const requestType: RequestTypes = this.existingModel.EditedRecord.RequestType;
    switch (requestType) {
      case RequestTypes.PartialDay:
        return GetHoursBetweenTwoTimes(
          this.existingModel.EditedRecord.StartDate,
          this.existingModel.EditedRecord.EndDate
        );
      case RequestTypes.SingleDay:
        return this.existingModel.EditedRecord.TimeOffDays[0].Hours;
      case RequestTypes.MultipleDays:
        return (
          this.existingModel.EditedRecord.TimeOffDays[0].Hours *
          GetDaysBetweenTwoDates(
            this.existingModel.EditedRecord.StartDate,
            this.existingModel.EditedRecord.EndDate
          )
        );
    }
  }
  private _initPreviousRequestHours() {
    const requestType: RequestTypes = this.existingModel.OriginalRecord.RequestType;
    switch (requestType) {
      case RequestTypes.PartialDay:
        return GetHoursBetweenTwoTimes(
          this.existingModel.OriginalRecord.StartDate,
          this.existingModel.OriginalRecord.EndDate
        );
      case RequestTypes.SingleDay:
        return this.existingModel.OriginalRecord.TimeOffDays[0].Hours;
      case RequestTypes.MultipleDays:
        return (
          this.existingModel.OriginalRecord.TimeOffDays[0].Hours *
          GetDaysBetweenTwoDates(
            this.existingModel.OriginalRecord.StartDate,
            this.existingModel.OriginalRecord.EndDate
          )
        );
    }
  }
  protected readonly AreDatesEqual = AreDatesEqual;
  protected readonly isEqual = isEqual;
}
